// ABOUTME: API module for monetization/billing and payment endpoints.
// ABOUTME: Exposes getMonetizareStatus() and payment initiation functions.

import apiClient from "./client";
import type { AccesResultDetail, StrategieReactivare } from "../utils/errorUtils";

export interface AbonamentResponse {
  id_abonament: string;
  id_utilizator: string;
  tip: string;
  status: string;
  data_inceput: string;
  data_sfarsit: string;
  numar_vehicule_la_creare: number;
  pret_luna_ron: number;
  discount_procent: number;
  auto_reinnoire: boolean;
  data_creare: string;
  data_actualizare: string;
}

export interface AvertismentExpirare {
  tip: "trial" | "acoperire_temporara" | "abonament";
  zile_ramase: number;
  data_sfarsit: string;
  data_start_abonament: string;
}

export interface StareMonetizare {
  is_grandfathered: boolean;
  perioada_gratuita_inceput: string;
  perioada_gratuita_sfarsit: string;
  numar_vehicule_active: number;
  numar_vehicule_cu_acces: number;
  numar_vehicule_fara_acces: number;
  limita_vehicule_gratuite: number;
  limita_vehicule_gratuite_baza: number;
  sloturi_extensie_luna_curenta: number;
  limita_vehicule_gratuite_efectiva: number;
  in_perioada_gratuita: boolean;
  vehicule_in_limita: boolean;
  abonament_activ: AbonamentResponse | null;
  abonamente_programate: AbonamentResponse[];
  vehicule_incluse_perioada_gratuita: {
    id_vehicul: string;
    nr_inmatriculare: string;
    activ: boolean;
    licenta_valabila_de_la: string | null;
    licenta_valabila_pana_la: string | null;
    are_exceptie_interval_global: boolean;
  }[];
  extensii_pe_vehicul: {
    id_vehicul: string;
    nr_inmatriculare: string;
    luni: string[];
  }[];
  luni_deblocate: string[];
  deblocari_retroactive_pe_vehicul: {
    id_vehicul: string;
    nr_inmatriculare: string;
    luni: string[];
  }[];
  necesita_actiune: boolean;
  motiv_actiune: string | null;
  vehicule_la_abonament: number | null;
  urmatoarea_plata_data: string | null;
  urmatoarea_plata_suma_eur: number | null;
  avertisment_expirare: AvertismentExpirare | null;
}

export type StatusPlata =
  | "in_asteptare"
  | "confirmata"
  | "esuata"
  | "rambursata";
export type TipPlata = "abonament" | "deblocare" | "vehicul";

export interface PlataIstoric {
  id_plata: string;
  data: string;
  tip: TipPlata;
  descriere: string;
  vehicule: string[];
  suma_eur: number;
  suma_ron: number;
  tva_eur: number;
  status: StatusPlata;
}

export interface CursEurRonResponse {
  eur_ron: string;
  data_curs: string | null;
  bnr_url: string;
}

export interface CoverageBoundsResponse {
  yesterday: string;
  last_covered_day: string | null;
  zi_start: string;
  earliest_generatable_day: string | null;
}

export interface InitiazaPlataResponse {
  id_plata: string;
  payment_url: string;
  suma_ron: number;
  suma_eur: number;
  curs_valutar: number;
}

export interface PlataStatusResponse {
  id_plata: string;
  status: StatusPlata;
  referinta_externa: string | null;
  suma_ron?: number | string | null;
}

export interface PreviewVehiculAliniere {
  id_vehicul: string;
  nr_inmatriculare: string;
  activ: boolean;
  nr_luni: number;
  luni?: string[];
  net_eur: string;
  tva_eur: string;
  total_eur: string;
}

export interface PreviewVehiculPierdereFoi {
  id_vehicul: string;
  nr_inmatriculare: string;
  nr_foi_sterse: number;
}

export interface PreviewPlataAbonament {
  data_inceput: string;
  nr_vehicule_contractuale: number;
  pret_lunar_net_eur: string;
  aliniere_net_eur: string;
  aliniere_tva_eur: string;
  aliniere_total_eur: string;
  vehicule_aliniere: PreviewVehiculAliniere[];
  luni_inceput_disponibile: string[];
  este_salt: boolean;
  vehicule_pierdere_foi: PreviewVehiculPierdereFoi[];
}

export interface PreviewActivareVehiculNou {
  disponibil: boolean;
  motiv: string | null;
  nr_luni: number;
  luni: string[];
  pret_net_eur: string;
  tva_eur: string;
  total_eur: string;
  data_sfarsit_abonament: string | null;
  credit_disponibil: boolean;
}

export type FacturareTip = "persoana_fizica" | "persoana_juridica";

export interface DateFacturareRequest {
  facturare_tip: FacturareTip;
  facturare_nume?: string;
  facturare_cui?: string | null;
  facturare_adresa?: string;
  facturare_id_firma?: string;
}

export const monetizareAPI = {
  /** Public: curs BNR pentru prețuri RON (nu necesită autentificare). */
  getCursEurRon: async (): Promise<CursEurRonResponse> => {
    const response = await apiClient.get<CursEurRonResponse>(
      "/api/v1/monetizare/curs-eur-ron",
    );
    return response.data;
  },

  getStatus: async (): Promise<StareMonetizare> => {
    const response = await apiClient.get<StareMonetizare>(
      "/api/v1/monetizare/status",
    );
    return response.data;
  },

  checkDayAccess: async (
    idVehicul: string,
    dayDate: string,
  ): Promise<AccesResultDetail> => {
    const response = await apiClient.get<AccesResultDetail>(
      "/api/v1/monetizare/check-day-access",
      {
        params: {
          id_vehicul: idVehicul,
          day_date: dayDate,
        },
      },
    );
    return response.data;
  },

  getCoverageBounds: async (
    idVehicul: string,
    ziStart: string,
  ): Promise<CoverageBoundsResponse> => {
    const response = await apiClient.get<CoverageBoundsResponse>(
      "/api/v1/monetizare/coverage-bounds",
      {
        params: {
          id_vehicul: idVehicul,
          zi_start: ziStart,
        },
      },
    );
    return response.data;
  },

  /** Preview the subscription monthly rate + one-time alignment cost (no payment). */
  previewSubscription: async (
    idVehiculeAliniere: string[] = [],
    dataInceput?: string,
    options?: {
      idVehiculReactivare?: string;
      strategieReactivare?: StrategieReactivare;
      luniRetro?: string[];
    },
  ): Promise<PreviewPlataAbonament> => {
    const response = await apiClient.post<PreviewPlataAbonament>(
      "/api/v1/plati/preview/abonament",
      {
        id_vehicule_aliniere: idVehiculeAliniere,
        ...(dataInceput ? { data_inceput: dataInceput } : {}),
        ...(options?.idVehiculReactivare
          ? { id_vehicul_reactivare: options.idVehiculReactivare }
          : {}),
        ...(options?.strategieReactivare
          ? { strategie_reactivare: options.strategieReactivare }
          : {}),
        ...(options?.luniRetro && options.luniRetro.length > 0
          ? { luni_retro_solicitate: options.luniRetro }
          : {}),
      },
    );
    return response.data;
  },

  /** Cost unic pentru un vehicul suplimentar, fără a porni plata. */
  previewNewVehicle: async (): Promise<PreviewActivareVehiculNou> => {
    const response = await apiClient.get<PreviewActivareVehiculNou>(
      "/api/v1/plati/preview/vehicul-nou",
    );
    return response.data;
  },

  initiateSubscription: async (
    tip: "lunar" | "trimestrial" | "anual",
    idVehiculReactivare?: string,
    luniRetroSolicitate: string[] = [],
    strategieReactivare?: StrategieReactivare,
    dateFacturare?: DateFacturareRequest,
    idVehiculeAliniere: string[] = [],
    dataInceput?: string,
  ): Promise<InitiazaPlataResponse> => {
    const response = await apiClient.post<InitiazaPlataResponse>(
      "/api/v1/plati/initiate/abonament",
      {
        tip,
        ...(idVehiculReactivare
          ? { id_vehicul_reactivare: idVehiculReactivare }
          : {}),
        ...(luniRetroSolicitate.length > 0
          ? { luni_retro_solicitate: luniRetroSolicitate }
          : {}),
        ...(strategieReactivare
          ? { strategie_reactivare: strategieReactivare }
          : {}),
        ...(idVehiculeAliniere.length > 0
          ? { id_vehicule_aliniere: idVehiculeAliniere }
          : {}),
        ...(dataInceput ? { data_inceput: dataInceput } : {}),
        ...(dateFacturare ? { date_facturare: dateFacturare } : {}),
      },
    );
    return response.data;
  },

  initiateUnlock: async (
    luna: string,
    id_vehicule: string[],
    dateFacturare?: DateFacturareRequest,
  ): Promise<InitiazaPlataResponse> => {
    const response = await apiClient.post<InitiazaPlataResponse>(
      "/api/v1/plati/initiate/deblocare",
      { luna, id_vehicule, ...(dateFacturare ? { date_facturare: dateFacturare } : {}) },
    );
    return response.data;
  },

  initiateUnlockBatch: async (
    luni: string[],
    id_vehicule: string[],
    dateFacturare?: DateFacturareRequest,
  ): Promise<InitiazaPlataResponse> => {
    const response = await apiClient.post<InitiazaPlataResponse>(
      "/api/v1/plati/initiate/deblocare-batch",
      { luni, id_vehicule, ...(dateFacturare ? { date_facturare: dateFacturare } : {}) },
    );
    return response.data;
  },

  toggleAutoReinnoire: async (
    auto_reinnoire: boolean,
  ): Promise<{ auto_reinnoire: boolean }> => {
    const response = await apiClient.patch<{ auto_reinnoire: boolean }>(
      "/api/v1/monetizare/auto-reinnoire",
      { auto_reinnoire },
    );
    return response.data;
  },

  initiateVehicleSurcharge: async (
    idVehicul?: string,
    lunaActivare?: string,
    strategieReactivare?: StrategieReactivare,
    idVehicule: string[] = [],
    dateFacturare?: DateFacturareRequest,
  ): Promise<InitiazaPlataResponse> => {
    const response = await apiClient.post<InitiazaPlataResponse>(
      "/api/v1/plati/initiate/vehicul-suplimentar",
      {
        ...(idVehicul ? { id_vehicul: idVehicul } : {}),
        ...(idVehicule.length > 0 ? { id_vehicule: idVehicule } : {}),
        ...(lunaActivare ? { luna_activare: lunaActivare } : {}),
        ...(strategieReactivare
          ? { strategie_reactivare: strategieReactivare }
          : {}),
        ...(dateFacturare ? { date_facturare: dateFacturare } : {}),
      },
    );
    return response.data;
  },

  getIstoricPlati: async (): Promise<PlataIstoric[]> => {
    const response = await apiClient.get<PlataIstoric[]>(
      "/api/v1/plati/istoric",
    );
    return response.data;
  },

  getPaymentStatus: async (paymentId: string): Promise<PlataStatusResponse> => {
    const response = await apiClient.get<PlataStatusResponse>(
      `/api/v1/plati/status/${paymentId}`,
    );
    return response.data;
  },
};
